import React, { useState, useEffect,  forwardRef, useImperativeHandle } from 'react';
import dayjs from 'dayjs';
import {
    Form,
    Input,
    Button,
    Collapse,
    Card,
    Divider,
    Typography,
    DatePicker,
    Table,
    Radio,
    Checkbox,
    Select,
    message
} from 'antd';
import {
    FaUser,
    FaUserTie,
    FaBook,
    FaBriefcase,
    FaHome,
    FaCalendarAlt,
    FaIdCard,
    FaEnvelope,
    FaPhone,
    FaMedkit,
    FaUniversity,
    FaAddressCard,
    FaFileAlt,
    FaCaretRight,
    FaCheck,
    FaFileSignature,
    FaHandshake,
    FaShieldAlt
} from 'react-icons/fa';
import { HiOfficeBuilding } from 'react-icons/hi';
import { useAuth } from "../../../context/AuthContext";
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { EmployeesContainer, MainCard, CustomCollapse, PanelHeader, PanelIcon, SectionForm, FormDivider, FormInput, IconInput, IconTextArea, SubmitButton, NameSection, AddressSection, StyledTable } from '../styledComp'
import { postEmployeesInfo, getPostEmployessInfo, clearEmployeeData } from "../../../redux/slice/hrFormSlice"
import moment from 'moment';
import PdfJoiningKit from './PdfJoiningKit';

const { Panel } = Collapse;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Regex patterns for validation
const NAME_REGEX = /^[A-Za-z\s]{1,50}$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_REGEX = /^[6-9]\d{9}$/;
const PAN_REGEX = /[A-Z]{5}[0-9]{4}[A-Z]{1}/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const PINCODE_REGEX = /^[1-9][0-9]{5}$/;
const AADHAAR_REGEX = /^[2-9]{1}[0-9]{11}$/;
const ACCOUNT_NUMBER_REGEX = /^[0-9]{9,18}$/;

const JoiningKit = forwardRef(({ user_id, searchTrigger, jwt_payload_group }, ref) => {
    if (user_id === null) return null;

    const [activeKey, setActiveKey] = useState(null);
    const [personalForm] = Form.useForm();
    const [educationForm] = Form.useForm();
    const [workForm] = Form.useForm();
    const [medicalForm] = Form.useForm();
    const [indemnityForm] = Form.useForm();
    const [ndaForm] = Form.useForm();
    const [authForm] = Form.useForm();
    const [loading, setLoading] = useState({
        personal: false,
        education: false,
        work: false,
        medical: false,
        indemnity: false,
        nda: false,
        auth: false
    });

    const [educationData, setEducationData] = useState([
        { key: '1', examination: 'X', degree: '', institution: '', board: '', year: '' },
        { key: '2', examination: 'XII', degree: '', institution: '', board: '', year: '' },
        { key: '3', examination: 'Graduation', degree: '', institution: '', board: '', year: '' },
        { key: '4', examination: 'Post Graduation', degree: '', institution: '', board: '', year: '' },
        { key: '5', examination: 'Any Diploma', degree: '', institution: '', board: '', year: '' },
    ]);

    const [workData, setWorkData] = useState([
        { key: '1', from: '', to: '', company: '', designation: '', jobNature: '', reason: '' },
        { key: '2', from: '', to: '', company: '', designation: '', jobNature: '', reason: '' },
        { key: '3', from: '', to: '', company: '', designation: '', jobNature: '', reason: '' },
        { key: '4', from: '', to: '', company: '', designation: '', jobNature: '', reason: '' },
    ]);

    const [isExperienced, setIsExperienced] = useState(false);

    const { authState } = useAuth();
    const dispatch = useDispatch();
    const jwtBearer = authState.token;
    const mainData = useSelector((state) => state?.hrForm?.emp_data || {});

    useEffect(() => {
        if (user_id === null) {
            dispatch(clearEmployeeData());
        } else {
            dispatch(getPostEmployessInfo({ jwtBearer, user_id }));
        }
    }, [dispatch, jwtBearer, user_id, searchTrigger]);

    useEffect(() => {
        if (mainData) {
            // Initialize forms with section data
            if (mainData.section1) {
                personalForm.setFieldsValue({
                    ...mainData.section1.personalInfo,
                    joiningDate: mainData.section1.personalInfo.joiningDate ? moment(mainData.section1.personalInfo.joiningDate) : null,
                    marriageAnniversary: mainData.section1.personalInfo.marriageAnniversary ? moment(mainData.section1.personalInfo.marriageAnniversary) : null,
                    actualDob: mainData.section1.personalInfo.actualDob ? moment(mainData.section1.personalInfo.actualDob) : null,
                    aadhaarDob: mainData.section1.personalInfo.aadhaarDob ? moment(mainData.section1.personalInfo.aadhaarDob) : null,
                    ...mainData.section1.addressInfo.currentAddress,
                    currentAddress: mainData.section1.addressInfo.currentAddress.address,
                    ...mainData.section1.addressInfo.permanentAddress,
                    permanentAddress: mainData.section1.addressInfo.permanentAddress.address,
                    ...mainData.section1.bankDetails,
                });
            }

            if (mainData.section2) {
                setEducationData(
                    educationData.map((item, index) => ({
                        ...item,
                        ...(mainData.section2.educationInfo[index] || {})
                    }))
                );
            }

            if (mainData.section3?.experiences) {
                const hasExperience = mainData.section3.experiences.some(exp => exp.company);
                setIsExperienced(hasExperience);

                const sortedExperiences = [...mainData.section3.experiences]
                    .sort((a, b) => {
                        const aFrom = a.period?.split(' - ')[0];
                        const bFrom = b.period?.split(' - ')[0];
                        return moment(bFrom, 'MM/YYYY').diff(moment(aFrom, 'MM/YYYY'));
                    })
                    .map((exp, index) => ({
                        key: (index + 1).toString(),
                        from: exp.period?.split(' - ')[0] || '',
                        to: exp.period?.split(' - ')[1] || '',
                        company: exp.company || '',
                        designation: exp.designation || '',
                        jobNature: exp.jobNature || '',
                        reason: exp.reason || ''
                    }));

                // Ensure we always have 4 work experience entries
                const defaultWorkData = [
                    { key: '1', from: '', to: '', company: '', designation: '', jobNature: '', reason: '' },
                    { key: '2', from: '', to: '', company: '', designation: '', jobNature: '', reason: '' },
                    { key: '3', from: '', to: '', company: '', designation: '', jobNature: '', reason: '' },
                    { key: '4', from: '', to: '', company: '', designation: '', jobNature: '', reason: '' }
                ];

                // Merge saved data with default empty entries
                const mergedWorkData = defaultWorkData.map((item, index) => {
                    return sortedExperiences[index] || item;
                });

                setWorkData(mergedWorkData);
                workForm.setFieldsValue({
                    totalExperience: mainData.section3.workExperience?.totalExperience,
                    experienceType: hasExperience ? 'experienced' : 'fresher'
                });
            }

            if (mainData.section4) {
                medicalForm.setFieldsValue({
                    spouseName: mainData.section4.familyInfo?.spouseName,
                    spouseDob: mainData.section4.familyInfo?.spouseDob ? moment(mainData.section4.familyInfo.spouseDob) : null,
                    child1Name: mainData.section4.familyInfo?.children?.[0]?.name,
                    child1Dob: mainData.section4.familyInfo?.children?.[0]?.dob ? moment(mainData.section4.familyInfo.children[0].dob) : null,
                    child2Name: mainData.section4.familyInfo?.children?.[1]?.name,
                    child2Dob: mainData.section4.familyInfo?.children?.[1]?.dob ? moment(mainData.section4.familyInfo.children[1].dob) : null,
                    child3Name: mainData.section4.familyInfo?.children?.[2]?.name,
                    child3Dob: mainData.section4.familyInfo?.children?.[2]?.dob ? moment(mainData.section4.familyInfo.children[2].dob) : null,
                    ...mainData.section4.emergencyContact,
                    ...mainData.section4.bankDetails,
                    declaration: mainData.section4.declaration?.isAgreed,
                    emergencyContactName: mainData.section4.emergencyContact?.name,
                    emergencyContactPhone: mainData.section4.emergencyContact?.phone,
                    emergencyContactRelationship: mainData.section4.emergencyContact?.relationship,
                });
            }

            if (mainData.section5) {
                const today = new Date();
                const initialValues = {
                    indemnityDay: today.getDate().toString(),
                    indemnityMonth: (today.getMonth() + 1).toString(),
                    indemnityYear: today.getFullYear().toString(),
                    indemnityName: mainData.section1?.personalInfo?.legalName || '',
                    indemnityFatherName: mainData.section1?.personalInfo?.fatherName || '',
                    witness1Name: 'Tanvi',
                    witness2Name: 'Abel Cherian Abey',
                    ...(mainData.section5 || {}),
                    signatureDate: mainData.section5?.signatureDate ? moment(mainData.section5.signatureDate) : null
                };

                indemnityForm.setFieldsValue(initialValues);
            }

            if (mainData.section6) {
                const today = new Date();
                const initialValues = {
                    ndaDay: today.getDate().toString(),
                    ndaMonth: (today.getMonth() + 1).toString(),
                    ndaYear: today.getFullYear().toString(),
                    ndaName: mainData.section1?.personalInfo?.legalName || '',
                    ndaFatherName: mainData.section1?.personalInfo?.fatherName || '',
                    ...(mainData.section6 || {}),
                    signatureDate: mainData.section6.signatureDate ? moment(mainData.section6.signatureDate) : null
                };
                ndaForm.setFieldsValue(initialValues);
            }

            if (mainData.section7) {
                authForm.setFieldsValue({
                    ...mainData.section7,
                    date: mainData.section7.date ? moment(mainData.section7.date) : null
                });
            }
        }
    }, [mainData]);

    const isSectionCompleted = (sectionNumber) => {
        return mainData[`is_section${sectionNumber}_completed`] === true;
    };

    const isAllSectionsCompleted = () => {
        return ([1, 2, 3, 4, 5, 6, 7].every(section => isSectionCompleted(section) && jwt_payload_group === '3'))
    };

    const renderField = (sectionNumber, element) => {
        if (isAllSectionsCompleted()) {
            return React.cloneElement(element, {
                disabled: true,
                ...element.props
            });
        }
        return React.cloneElement(element, {
            disabled: false,
            ...element.props
        });
    };

    const onCollapseChange = (keys) => {
        setActiveKey(keys);
    };

    const handleCompleteAll = () => {
        if (isAllSectionsCompleted()) {
            message.success('All sections are already completed!');
            return;
        }

        const allCompleted = [1, 2, 3, 4, 5, 6, 7].every(section => isSectionCompleted(section));
        if (!allCompleted) {
            message.error('Please complete all sections before submitting!');
            return;
        }

        message.success('All sections completed successfully!');
    };

    const handlePersonalSubmit = async (values) => {
        setLoading(prev => ({ ...prev, personal: true }));
        const requestBody = {
            section1: {
                personalInfo: {
                    legalName: values.legalName,
                    firstName: values.firstName,
                    middleName: values.middleName,
                    lastName: values.lastName,
                    fatherName: values.fatherName,
                    joiningDate: '01-04-2025',
                    gender: values.gender,
                    maritalStatus: values.maritalStatus,
                    marriageAnniversary: values.marriageAnniversary?.format('YYYY-MM-DD'),
                    actualDob: values.actualDob?.format('YYYY-MM-DD'),
                    aadhaarDob: values.aadhaarDob?.format('YYYY-MM-DD'),
                    bloodGroup: values.bloodGroup,
                    contactNumber: values.contactNumber,
                    aadhaarContactNumber: values.aadhaarContactNumber,
                    emailId: values.emailId,
                    panNumber: values.panNumber
                },
                addressInfo: {
                    currentAddress: {
                        cAddress: values.cAddress,
                        cCity: values.cCity,
                        cDistrict: values.cCity,
                        cState: values.cCity,
                        cPincode: values.cPincode
                    },
                    permanentAddress: {
                        pAddress: values.pAddress,
                        pCity: values.pCity,
                        pDistrict: values.pDistrict,
                        pState: values.pState,
                        pPincode: values.pPincode
                    }
                },
                bankDetails: {
                    bankName: values.bankName,
                    bankAccountNumber: values.bankAccountNumber,
                    ifscCode: values.ifscCode
                },
            },
            is_section1_completed: true
        };

        try {
            const response = await dispatch(postEmployeesInfo({
                jwtBearer,
                requestBody,
                user_id
            }));

            await dispatch(getPostEmployessInfo({ jwtBearer, user_id }));

            if (response.error) {
                throw new Error('Failed to save personal information');
            }

            message.success('Personal information saved successfully');
            setActiveKey(null);
        } catch (error) {
            message.error('Error saving personal information');
            console.error('Error:', error);
        } finally {
            setLoading(prev => ({ ...prev, personal: false }));
        }
    };

    const handleEducationSubmit = async (values) => {
        setLoading(prev => ({ ...prev, education: true }));
        await educationForm.validateFields();
        const requestBody = {
            section2: {
                educationInfo: educationData.map(edu => ({
                    examination: edu.examination,
                    degree: edu.degree,
                    institution: edu.institution,
                    board: edu.board,
                    year: edu.year
                }))
            },
            is_section2_completed: true
        };

        try {
            const response = await dispatch(postEmployeesInfo({
                jwtBearer,
                requestBody,
                user_id
            }));

            await dispatch(getPostEmployessInfo({ jwtBearer, user_id }));

            if (response.error) {
                throw new Error('Failed to save education information');
            }

            message.success('Education information saved successfully');
            setActiveKey(null);
        } catch (error) {
            message.error('Error saving education information');
            console.error('Error:', error);
        } finally {
            setLoading(prev => ({ ...prev, education: false }));
        }
    };

    const handleWorkSubmit = async (values) => {
        setLoading(prev => ({ ...prev, work: true }));

        // Validate at least one experience is filled if experienced
        if (isExperienced && !workData.some(work => work.company)) {
            message.error('Please fill at least one work experience');
            setLoading(prev => ({ ...prev, work: false }));
            return;
        }

        // Filter out empty rows before submitting
        const experiencesToSubmit = isExperienced ?
            workData
                .filter(work => work.company) // Only include entries with company name
                .sort((a, b) => moment(b.from, 'MM/YYYY').diff(moment(a.from, 'MM/YYYY'))) // Sort by from date (newest first)
                .map(work => ({
                    period: `${work.from} - ${work.to}`,
                    company: work.company,
                    designation: work.designation,
                    jobNature: work.jobNature,
                    reason: work.reason
                })) : [];

        const requestBody = {
            section3: {
                experiences: experiencesToSubmit,
                workExperience: {
                    totalExperience: values.totalExperience || '0 years'
                }
            },
            is_section3_completed: true
        };

        try {
            const response = await dispatch(postEmployeesInfo({
                jwtBearer,
                requestBody,
                user_id
            }));

            await dispatch(getPostEmployessInfo({ jwtBearer, user_id }));

            if (response.error) {
                throw new Error('Failed to save work experience');
            }

            message.success('Work experience saved successfully');
            setActiveKey(null);
        } catch (error) {
            message.error('Error saving work experience');
            console.error('Error:', error);
        } finally {
            setLoading(prev => ({ ...prev, work: false }));
        }
    };

    const handleMedicalSubmit = async (values) => {
        setLoading(prev => ({ ...prev, medical: true }));

        // Prepare children array only for children with names
        const children = [];
        if (values.child1Name) {
            children.push({
                name: values.child1Name,
                dob: values.child1Dob?.format('YYYY-MM-DD')
            });
        }
        if (values.child2Name) {
            children.push({
                name: values.child2Name,
                dob: values.child2Dob?.format('YYYY-MM-DD')
            });
        }
        if (values.child3Name) {
            children.push({
                name: values.child3Name,
                dob: values.child3Dob?.format('YYYY-MM-DD')
            });
        }

        const requestBody = {
            section4: {
                familyInfo: {
                    spouseName: values.spouseName,
                    spouseDob: values.spouseDob?.format('YYYY-MM-DD'),
                    children
                },
                emergencyContact: {
                    name: values.emergencyContactName,
                    phone: values.emergencyContactPhone,
                    relationship: values.emergencyContactRelationship
                },
                declaration: {
                    isAgreed: values.declaration,
                    // date: values.signatureDate?.format('YYYY-MM-DD')
                }
            },
            is_section4_completed: true
        };

        try {
            const response = await dispatch(postEmployeesInfo({
                jwtBearer,
                requestBody,
                user_id
            }));

            await dispatch(getPostEmployessInfo({ jwtBearer, user_id }));

            if (response.error) {
                throw new Error('Failed to save medical policy details');
            }

            message.success('Medical policy details saved successfully');
            setActiveKey(null);
        } catch (error) {
            message.error('Error saving medical policy details');
            console.error('Error:', error);
        } finally {
            setLoading(prev => ({ ...prev, medical: false }));
        }
    };

    const handleIndemnitySubmit = async (values) => {
        setLoading(prev => ({ ...prev, indemnity: true }));
        const requestBody = {
            section5: {
                indemnityDay: values.indemnityDay,
                indemnityMonth: values.indemnityMonth,
                indemnityYear: values.indemnityYear,
                indemnityName: values.legalName,
                indemnityFatherName: values.fatherName,
                aadhaarNumber: values.aadhaarNumber,
                isAgreed: values.isAgreed,
                // signatureDate: values.signatureDate?.format('YYYY-MM-DD'),
                witness1Name: values.witness1Name,
                witness1Address: values.witness1Address,
                witness2Name: values.witness2Name,
                witness2Address: values.witness2Address
            },
            is_section5_completed: true
        };

        try {
            const response = await dispatch(postEmployeesInfo({
                jwtBearer,
                requestBody,
                user_id
            }));

            await dispatch(getPostEmployessInfo({ jwtBearer, user_id }));

            if (response.error) {
                throw new Error('Failed to save indemnity bond');
            }

            message.success('Indemnity bond saved successfully');
            setActiveKey(null);
        } catch (error) {
            message.error('Error saving indemnity bond');
            console.error('Error:', error);
        } finally {
            setLoading(prev => ({ ...prev, indemnity: false }));
        }
    };

    const handleNDASubmit = async (values) => {
        setLoading(prev => ({ ...prev, nda: true }));
        const requestBody = {
            section6: {
                ndaDay: values.ndaDay,
                ndaMonth: values.ndaMonth,
                ndaYear: values.ndaYear,
                ndaName: values.ndaName,
                ndaFatherName: values.ndaFatherName,
                ndaAddress: values.ndaAddress,
                aadhaarNumber: values.aadhaarNumber,
                isAgreed: values.isAgreed,
                signatureDate: values.signatureDate?.format('YYYY-MM-DD'),
                employeeName: values.employeeName,
                place: values.place,
                designation: values.designation,
                witness1Name: values.witness1Name,
                witness2Name: values.witness2Name,
                ndaDesignation: values.ndaDesignation
            },
            is_section6_completed: true
        };

        try {
            const response = await dispatch(postEmployeesInfo({
                jwtBearer,
                requestBody,
                user_id
            }));

            await dispatch(getPostEmployessInfo({ jwtBearer, user_id }));

            if (response.error) {
                throw new Error('Failed to save NDA agreement');
            }

            message.success('NDA agreement saved successfully');
            setActiveKey(null);
        } catch (error) {
            message.error('Error saving NDA agreement');
            console.error('Error:', error);
        } finally {
            setLoading(prev => ({ ...prev, nda: false }));
        }
    };

    const handleAuthSubmit = async (values) => {
        setLoading(prev => ({ ...prev, auth: true }));
        const requestBody = {
            section7: {
                isAgreed: values.isAgreed,
                // date: values.date?.format('YYYY-MM-DD'),
                // place: values.place,
                name: values.name
            },
            is_section7_completed: true
        };

        try {
            const response = await dispatch(postEmployeesInfo({
                jwtBearer,
                requestBody,
                user_id
            }));

            await dispatch(getPostEmployessInfo({ jwtBearer, user_id }));

            if (response.error) {
                throw new Error('Failed to save authorization form');
            }

            message.success('Authorization form saved successfully');
            setActiveKey(null);
        } catch (error) {
            message.error('Error saving authorization form');
            console.error('Error:', error);
        } finally {
            setLoading(prev => ({ ...prev, auth: false }));
        }
    };

    const handleEducationChange = (key, field, value) => {
        if (isAllSectionsCompleted()) return;

        const newData = educationData.map(item => {
            if (item.key === key) {
                return { ...item, [field]: value };
            }
            return item;
        });
        setEducationData(newData);
    };

    const handleWorkChange = (key, field, value) => {
        if (isAllSectionsCompleted()) return;

        const newData = workData.map(item => {
            if (item.key === key) {
                return { ...item, [field]: value };
            }
            return item;
        });
        setWorkData(newData);
    };

    const handleExperienceTypeChange = (e) => {
        setIsExperienced(e.target.value === 'experienced');
    };

    const handleSameAsCurrentChange = (e) => {
        if (e.target.checked) {
            const currentValues = personalForm.getFieldsValue([
                'cAddress',
                'cCity',
                'cDistrict',
                'cState',
                'cPincode'
            ]);

            personalForm.setFieldsValue({
                pAddress: currentValues.cAddress,
                pCity: currentValues.cCity,
                pDistrict: currentValues.cDistrict,
                pState: currentValues.cState,
                pPincode: currentValues.cPincode
            });
        } else {
            personalForm.setFieldsValue({
                pAddress: '',
                pCity: '',
                pDistrict: '',
                pState: '',
                pPincode: ''
            });
        }
    };

    const handlePeriodChange = (index, type, dateString) => {
        const newWorkData = [...workData];
        if (type === 'from') {
            newWorkData[index].from = dateString;
        } else {
            newWorkData[index].to = dateString;
        }
        setWorkData(newWorkData);
    };

    const educationColumns = [
        {
            title: 'Examination Passed',
            dataIndex: 'examination',
            key: 'examination',
            render: (text) => <span>{text}</span>
        },
        {
            title: 'Degree/Stream',
            dataIndex: 'degree',
            key: 'degree',
            render: (_, record) => (
                <Form.Item
                    name={`degree-${record.key}`}
                    rules={[
                        {
                            pattern: /^[a-zA-Z0-9\s\-.,()&]+$/,
                            message: 'Please enter a valid degree name!'
                        }
                    ]}
                    initialValue={record.degree}
                >
                    <Input
                        value={record.degree}
                        onChange={(e) => handleEducationChange(record.key, 'degree', e.target.value)}
                        placeholder="Enter degree/stream"
                        disabled={isAllSectionsCompleted()}
                    />
                </Form.Item>
            ),
        },
        {
            title: 'School/College',
            dataIndex: 'institution',
            key: 'institution',
            render: (_, record) => (
                <Form.Item
                    name={`institution-${record.key}`}
                    rules={[
                        {
                            pattern: NAME_REGEX,
                            message: 'Please enter a valid institution name!'
                        }
                    ]}
                    initialValue={record.institution}
                >
                    <Input
                        value={record.institution}
                        onChange={(e) => handleEducationChange(record.key, 'institution', e.target.value)}
                        placeholder="Enter school/college"
                        disabled={isAllSectionsCompleted()}
                    />
                </Form.Item>
            ),
        },
        {
            title: 'Board/University',
            dataIndex: 'board',
            key: 'board',
            render: (_, record) => (
                <Form.Item
                    name={`board-${record.key}`}
                    rules={[
                        {
                            pattern: NAME_REGEX,
                            message: 'Please enter a valid board/university name!'
                        }
                    ]}
                    initialValue={record.board}
                >
                    <Input
                        value={record.board}
                        onChange={(e) => handleEducationChange(record.key, 'board', e.target.value)}
                        placeholder="Enter board/university"
                        disabled={isAllSectionsCompleted()}
                    />
                </Form.Item>
            ),
        },
        {
            title: 'Year Passed',
            dataIndex: 'year',
            key: 'year',
            render: (_, record) => (
                <Form.Item
                    name={`year-${record.key}`}
                    rules={[
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                // Only validate if there's data in other fields
                                const hasData = record.degree || record.institution || record.board;
                                if (!hasData || !value) {
                                    return Promise.resolve();
                                }
                                const currentYear = new Date().getFullYear();
                                const minYear = 1950;
                                if (value && value.year() >= minYear && value.year() <= currentYear) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error(`Year must be between ${minYear} and ${currentYear}`));
                            }
                        })
                    ]}
                    initialValue={record.year ? moment(record.year, 'YYYY') : null}
                >
                    <DatePicker
                        picker="year"
                        value={record.year ? moment(record.year, 'YYYY') : null}
                        onChange={(date, dateString) => handleEducationChange(record.key, 'year', dateString)}
                        disabled={isAllSectionsCompleted()}
                        style={{ width: '100%' }}
                    />
                </Form.Item>
            ),
        },
    ];

    const workColumns = [
        {
            title: 'From',
            dataIndex: 'from',
            key: 'from',
            render: (_, record, index) => (
                <DatePicker
                    picker="month"
                    format="MM/YYYY"
                    placeholder="From"
                    value={record.from ? moment(record.from, 'MM/YYYY') : null}
                    onChange={(date, dateString) => handlePeriodChange(index, 'from', dateString)}
                    style={{ width: '100%' }}
                    disabled={isAllSectionsCompleted()}
                />
            ),
        },
        {
            title: 'To',
            dataIndex: 'to',
            key: 'to',
            render: (_, record, index) => (
                <DatePicker
                    picker="month"
                    format="MM/YYYY"
                    placeholder="To"
                    value={record.to ? moment(record.to, 'MM/YYYY') : null}
                    onChange={(date, dateString) => handlePeriodChange(index, 'to', dateString)}
                    style={{ width: '100%' }}
                    disabled={isAllSectionsCompleted()}
                />
            ),
        },
        {
            title: 'Company Name',
            dataIndex: 'company',
            key: 'company',
            render: (_, record) => (
                <Input
                    value={record.company}
                    onChange={(e) => handleWorkChange(record.key, 'company', e.target.value)}
                    placeholder="Enter company name"
                    disabled={isAllSectionsCompleted()}
                />
            ),
        },
        {
            title: 'Designation',
            dataIndex: 'designation',
            key: 'designation',
            render: (_, record) => (
                <Input
                    value={record.designation}
                    onChange={(e) => handleWorkChange(record.key, 'designation', e.target.value)}
                    placeholder="Enter designation"
                    disabled={isAllSectionsCompleted()}
                />
            ),
        },
        {
            title: 'Nature of Job(Full-Time/Intern/Contract)',
            dataIndex: 'jobNature',
            key: 'jobNature',
            render: (_, record) => (
                <Input
                    value={record.jobNature}
                    onChange={(e) => handleWorkChange(record.key, 'jobNature', e.target.value)}
                    placeholder="Describe job nature"
                    disabled={isAllSectionsCompleted()}
                />
            ),
        },
        {
            title: 'Reason for leaving',
            dataIndex: 'reason',
            key: 'reason',
            render: (_, record) => (
                <Input
                    value={record.reason}
                    onChange={(e) => handleWorkChange(record.key, 'reason', e.target.value)}
                    placeholder="Reason for leaving"
                    disabled={isAllSectionsCompleted()}
                />
            ),
        },
    ];

    useImperativeHandle(ref, () => ({
        resetAllForms: () => {
          personalForm.resetFields();
          educationForm.resetFields();
          workForm.resetFields();
          medicalForm.resetFields();
          indemnityForm.resetFields();
          ndaForm.resetFields();
          authForm.resetFields();
        }
      }));

    return (
        <EmployeesContainer>
            <MainCard
            title={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ margin: 0 }}>Employee Joining Kit Information Form</span>
                <PdfJoiningKit formData={mainData} />
            </div>}
            bordered={false}
            >
            <CustomCollapse
                accordion
                activeKey={activeKey}
                onChange={onCollapseChange}
                expandIcon={({ isActive }) => <FaCaretRight style={{ transform: `rotate(${isActive ? 90 : 0}deg)`, transition: 'transform 0.3s' }} />}
            >
                {/* Personal Information Section */}
                <Panel
                    header={
                        <PanelHeader>
                            <PanelIcon><FaUser /></PanelIcon>
                            <span>Personal Information</span>
                            {isSectionCompleted(1) && <Text type="success" style={{ marginLeft: 'auto' }}>Completed</Text>}
                        </PanelHeader>
                    }
                    key="1"
                >
                    <SectionForm
                        form={personalForm}
                        layout="vertical"
                        onFinish={handlePersonalSubmit}
                        autoComplete="off"
                    >
                        <FormDivider orientation="left" orientationMargin="0">
                            Employee Name
                        </FormDivider>

                        <NameSection>
                            <Form.Item
                                label="Legal Full Name (As Per Aadhaar)"
                                name="legalName"
                                rules={[
                                    { required: true, message: 'Please input your legal name!' },
                                    { pattern: NAME_REGEX, message: 'Please enter a valid name!' }
                                ]}
                            >
                                {renderField(1,
                                    <IconInput
                                        placeholder="Legal name"
                                        prefix={<FaIdCard />}
                                    />
                                )}
                            </Form.Item>

                            <Form.Item
                                label="First Name (As Per Aadhaar)"
                                name="firstName"
                                rules={[
                                    { required: true, message: 'Please input your first name!' },
                                    { pattern: NAME_REGEX, message: 'Please enter a valid name!' }
                                ]}
                            >
                                {renderField(1,
                                    <IconInput
                                        placeholder="First name"
                                        prefix={<FaUser />}
                                    />
                                )}
                            </Form.Item>

                            <Form.Item
                                label="Middle Name (As Per Aadhaar)"
                                name="middleName"
                                rules={[
                                    { pattern: NAME_REGEX, message: 'Please enter a valid name!' }
                                ]}
                            >
                                {renderField(1,
                                    <FormInput
                                        placeholder="Middle name"
                                    />
                                )}
                            </Form.Item>

                            <Form.Item
                                label="Last Name (As Per Aadhaar)"
                                name="lastName"
                                rules={[
                                    { pattern: NAME_REGEX, message: 'Please enter a valid name!' }
                                ]}
                            >
                                {renderField(1,
                                    <FormInput
                                        placeholder="Last name"
                                    />
                                )}
                            </Form.Item>
                        </NameSection>

                        <FormDivider orientation="left" orientationMargin="0">
                            Family Information
                        </FormDivider>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <Form.Item
                                label="Father's Name (As Per Aadhaar)"
                                name="fatherName"
                                rules={[
                                    { required: true, message: 'Please input your father\'s name!' },
                                    { pattern: NAME_REGEX, message: 'Please enter a valid name!' }
                                ]}
                            >
                                {renderField(1,
                                    <IconInput
                                        placeholder="Father's name"
                                        prefix={<FaUserTie />}
                                    />
                                )}
                            </Form.Item>
                        </div>

                        <FormDivider orientation="left" orientationMargin="0">
                            Personal Details
                        </FormDivider>

                        <Form.Item
                            label="Gender"
                            name="gender"
                            rules={[
                                { required: true, message: 'Please select gender!' }
                            ]}
                        >
                            {renderField(1,
                                <Radio.Group>
                                    <Radio value="male">Male</Radio>
                                    <Radio value="female">Female</Radio>
                                    <Radio value="other">Other</Radio>
                                </Radio.Group>
                            )}
                        </Form.Item>

                        <Form.Item
                            label="Marital Status"
                            name="maritalStatus"
                            rules={[
                                { required: true, message: 'Please select marital status!' }
                            ]}
                        >
                            {renderField(1,
                                <Radio.Group>
                                    <Radio value="married">Married</Radio>
                                    <Radio value="single">Single</Radio>
                                    <Radio value="other">Other</Radio>
                                </Radio.Group>
                            )}
                        </Form.Item>

                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.maritalStatus !== currentValues.maritalStatus}
                        >
                            {({ getFieldValue }) => {
                                const isMarried = getFieldValue('maritalStatus') === 'married';
                                return isMarried ? (
                                    <Form.Item
                                        label="Marriage Anniversary Date"
                                        name="marriageAnniversary"
                                        rules={[
                                            { required: true, message: 'Please provide marriage anniversary date!' }
                                        ]}
                                    >
                                        {renderField(1,
                                            <DatePicker
                                                style={{ width: '100%' }}
                                                format="DD/MM/YYYY"
                                                placeholder="DD/MM/YYYY"
                                            />
                                        )}
                                    </Form.Item>
                                ) : null;
                            }}
                        </Form.Item>

                        <Form.Item
                            label="Date of Birth (Actual)"
                            name="actualDob"
                            rules={[
                                { required: true, message: 'Please select date of birth!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value) {
                                            return Promise.resolve();
                                        }
                                        if (value.isAfter(new Date())) {
                                            return Promise.reject(new Error('Please select a valid Date of Birth!'));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            {renderField(1,
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    placeholder="DD/MM/YYYY"
                                />
                            )}
                        </Form.Item>

                        <Form.Item
                            label="Date of Birth (As Per Aadhaar)"
                            name="aadhaarDob"
                            rules={[
                                { required: true, message: 'Please select date of birth as per Aadhaar!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value) {
                                            return Promise.resolve();
                                        }
                                        if (value.isAfter(new Date())) {
                                            return Promise.reject(new Error('Please select a valid Date of Birth as per Aadhaar!'));
                                        }
                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            {renderField(1,
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    placeholder="DD/MM/YYYY"
                                />
                            )}
                        </Form.Item>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <Form.Item
                                label="Blood Group"
                                name="bloodGroup"
                                rules={[
                                    { required: true, message: 'Please select blood group!' }
                                ]}
                            >
                                {renderField(1,
                                    <Select placeholder="Select blood group">
                                        <Option value="A+">A+</Option>
                                        <Option value="A-">A-</Option>
                                        <Option value="B+">B+</Option>
                                        <Option value="B-">B-</Option>
                                        <Option value="AB+">AB+</Option>
                                        <Option value="AB-">AB-</Option>
                                        <Option value="O+">O+</Option>
                                        <Option value="O-">O-</Option>
                                    </Select>
                                )}
                            </Form.Item>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="Contact Number"
                                    name="contactNumber"
                                    rules={[
                                        { required: true, message: 'Please input your contact number!' },
                                        {
                                            pattern: PHONE_REGEX,
                                            message: 'Please enter valid 10-digit mobile number!'
                                        }
                                    ]}
                                >
                                    {renderField(1,
                                        <IconInput placeholder="Mobile/Landline" prefix={<FaPhone />} />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="Contact Number (Aadhaar Linked Contact Number)"
                                    name="aadhaarContactNumber"
                                    rules={[
                                        { required: true, message: 'Please input your contact number!' },
                                        {
                                            pattern: PHONE_REGEX,
                                            message: 'Please enter valid 10-digit mobile number!'
                                        }
                                    ]}
                                >
                                    {renderField(1,
                                        <IconInput placeholder="Mobile/Landline (As Per Aadhaar)" prefix={<FaPhone />} />
                                    )}
                                </Form.Item>
                            </div>
                        </div>

                        <Form.Item
                            label="Email ID"
                            name="emailId"
                            rules={[
                                { required: true, message: 'Please input your email!' },
                                { type: 'email', message: 'Please enter valid email!' },
                            ]}
                        >
                            {renderField(1,
                                <IconInput placeholder="Email ID" prefix={<FaEnvelope />} />
                            )}
                        </Form.Item>

                        <Form.Item
                            label="PAN Number"
                            name="panNumber"
                            rules={[
                                { required: true, message: 'Please input your PAN number!' },
                                {
                                    pattern: PAN_REGEX,
                                    message: 'Please enter valid PAN number!'
                                }
                            ]}
                        >
                            {renderField(1,
                                <IconInput placeholder="PAN Number" prefix={<FaFileAlt />} style={{ textTransform: 'uppercase' }} />
                            )}
                        </Form.Item>

                        <FormDivider orientation="left" orientationMargin="0">
                            Address Information
                        </FormDivider>
                        <AddressSection>
                            <div>
                                <Title level={5} style={{ marginBottom: 16 }}>Current Address</Title>
                                <Form.Item
                                    name="cAddress"
                                >
                                    {renderField(1,
                                        <IconTextArea
                                            placeholder="Full address"
                                            prefix={<FaHome />}
                                            rows={3}
                                        />
                                    )}
                                </Form.Item>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <Form.Item
                                        name="cCity"
                                        rules={[
                                            { pattern: NAME_REGEX, message: 'Please enter valid city name!' }
                                        ]}
                                    >
                                        {renderField(1,
                                            <FormInput placeholder="City" />
                                        )}
                                    </Form.Item>
                                    <Form.Item
                                        name="cDistrict"
                                        rules={[
                                            { pattern: NAME_REGEX, message: 'Please enter valid district name!' }
                                        ]}
                                    >
                                        {renderField(1,
                                            <FormInput placeholder="District" />
                                        )}
                                    </Form.Item>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <Form.Item
                                        name="cState"
                                        rules={[
                                            { pattern: NAME_REGEX, message: 'Please enter valid state name!' }
                                        ]}
                                    >
                                        {renderField(1,
                                            <FormInput placeholder="State" />
                                        )}
                                    </Form.Item>
                                    <Form.Item
                                        name="cPincode"
                                        rules={[
                                            { pattern: PINCODE_REGEX, message: 'Please enter valid 6-digit pincode!' }
                                        ]}
                                    >
                                        {renderField(1,
                                            <FormInput placeholder="Pincode" />
                                        )}
                                    </Form.Item>
                                </div>
                            </div>

                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                    <Title level={5}>Permanent Address</Title>
                                    <Checkbox
                                        onChange={handleSameAsCurrentChange}
                                    >
                                        Same as current address
                                    </Checkbox>
                                </div>
                                <Form.Item
                                    name="pAddress"
                                    rules={[
                                        { required: true, message: 'Please input your permanent address!' }
                                    ]}
                                >
                                    {renderField(1,
                                        <IconTextArea
                                            placeholder="Full address"
                                            prefix={<FaHome />}
                                            rows={3}
                                        />
                                    )}
                                </Form.Item>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <Form.Item
                                        name="pCity"
                                        rules={[
                                            { required: true, message: 'Please input your city!' },
                                            { pattern: NAME_REGEX, message: 'Please enter valid city name!' }
                                        ]}
                                    >
                                        {renderField(1,
                                            <FormInput placeholder="City" />
                                        )}
                                    </Form.Item>
                                    <Form.Item
                                        name="pDistrict"
                                        rules={[
                                            { required: true, message: 'Please input your district!' },
                                            { pattern: NAME_REGEX, message: 'Please enter valid district name!' }
                                        ]}
                                    >
                                        {renderField(1,
                                            <FormInput placeholder="District" />
                                        )}
                                    </Form.Item>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <Form.Item
                                        name="pState"
                                        rules={[
                                            { required: true, message: 'Please input your state!' },
                                            { pattern: NAME_REGEX, message: 'Please enter valid state name!' }
                                        ]}
                                    >
                                        {renderField(1,
                                            <FormInput placeholder="State" />
                                        )}
                                    </Form.Item>
                                    <Form.Item
                                        name="pPincode"
                                        rules={[
                                            { required: true, message: 'Please input your pincode!' },
                                            { pattern: PINCODE_REGEX, message: 'Please enter valid 6-digit pincode!' }
                                        ]}
                                    >
                                        {renderField(1,
                                            <FormInput placeholder="Pincode" />
                                        )}
                                    </Form.Item>
                                </div>
                            </div>
                        </AddressSection>

                        <FormDivider orientation="left" orientationMargin="0">
                            Bank Details
                        </FormDivider>

                        <Form.Item
                            label="Bank Name"
                            name="bankName"
                            rules={[
                                { required: true, message: 'Please input your bank name!' }
                            ]}
                        >
                            {renderField(1,
                                <IconInput placeholder="Bank Name" prefix={<HiOfficeBuilding />} />
                            )}
                        </Form.Item>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <Form.Item
                                label="Bank Account Number"
                                name="bankAccountNumber"
                                rules={[
                                    { required: true, message: 'Please input your account number!' },
                                    {
                                        pattern: ACCOUNT_NUMBER_REGEX,
                                        message: 'Please enter valid account number (9-18 digits)!'
                                    }
                                ]}
                            >
                                {renderField(1,
                                    <FormInput placeholder="Account Number" />
                                )}
                            </Form.Item>
                            <Form.Item
                                label="IFSC Code"
                                name="ifscCode"
                                rules={[
                                    { required: true, message: 'Please input your IFSC code!' },
                                    {
                                        pattern: IFSC_REGEX,
                                        message: 'Please enter valid IFSC code!'
                                    }
                                ]}
                            >
                                {renderField(1,
                                    <FormInput placeholder="IFSC Code" style={{ textTransform: 'uppercase' }} />
                                )}
                            </Form.Item>
                        </div>

                        {!isAllSectionsCompleted() && (
                            <Form.Item>
                                <SubmitButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading.personal}
                                >
                                    {isSectionCompleted(1) ? 'Update Personal Information' : 'Save Personal Information'}
                                </SubmitButton>
                            </Form.Item>
                        )}
                    </SectionForm>
                </Panel>

                {/* Education Section */}
                <Panel
                    header={
                        <PanelHeader>
                            <PanelIcon><FaBook /></PanelIcon>
                            <span>Educational Information</span>
                            {isSectionCompleted(2) && <Text type="success" style={{ marginLeft: 'auto' }}>Completed</Text>}
                        </PanelHeader>
                    }
                    key="2"
                >
                    <SectionForm
                        form={educationForm}
                        layout="vertical"
                        onFinish={handleEducationSubmit}
                        autoComplete="off"
                    >
                        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                            Please fill in your educational qualifications
                        </Text>

                        <StyledTable
                            columns={educationColumns}
                            dataSource={educationData}
                            pagination={false}
                            bordered
                        />

                        {!isAllSectionsCompleted() && (
                            <Form.Item>
                                <SubmitButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading.education}
                                >
                                    {isSectionCompleted(2) ? 'Update Education Information' : 'Save Education Information'}
                                </SubmitButton>
                            </Form.Item>
                        )}
                    </SectionForm>
                </Panel>

                {/* Work Experience Section */}
                <Panel
                    header={
                        <PanelHeader>
                            <PanelIcon><FaBriefcase /></PanelIcon>
                            <span>Work Experience</span>
                            {isSectionCompleted(3) && <Text type="success" style={{ marginLeft: 'auto' }}>Completed</Text>}
                        </PanelHeader>
                    }
                    key="3"
                >
                    <SectionForm
                        form={workForm}
                        layout="vertical"
                        onFinish={handleWorkSubmit}
                        autoComplete="off"
                    >
                        <Form.Item
                            label="Are you a Fresher or Experienced?"
                            name="experienceType"
                            rules={[
                                { required: true, message: 'Please select your experience type!' }
                            ]}
                        >
                            <Radio.Group onChange={handleExperienceTypeChange}>
                                <Radio value="fresher">Fresher</Radio>
                                <Radio value="experienced">Experienced</Radio>
                            </Radio.Group>
                        </Form.Item>

                        {isExperienced && (
                            <>
                                <StyledTable
                                    columns={workColumns}
                                    dataSource={workData}
                                    pagination={false}
                                    bordered
                                />
                                <Form.Item
                                    label="Total Work Experience"
                                    name="totalExperience"
                                    style={{ marginTop: 24 }}
                                    rules={[
                                        { required: true, message: 'Please input your total work experience!' }
                                    ]}
                                >
                                    {renderField(3,
                                        <IconInput
                                            placeholder="Years/Months"
                                            prefix={<FaCalendarAlt />}
                                        />
                                    )}
                                </Form.Item>
                            </>
                        )}

                        {!isAllSectionsCompleted() && (
                            <Form.Item>
                                <SubmitButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading.work}
                                >
                                    {isSectionCompleted(3) ? 'Update Work Experience' : 'Save Work Experience'}
                                </SubmitButton>
                            </Form.Item>
                        )}
                    </SectionForm>
                </Panel>

                {/* Medical Policy Section */}
                <Panel
                    header={
                        <PanelHeader>
                            <PanelIcon><FaMedkit /></PanelIcon>
                            <span>Medical Policy Details</span>
                            {isSectionCompleted(4) && <Text type="success" style={{ marginLeft: 'auto' }}>Completed</Text>}
                        </PanelHeader>
                    }
                    key="4"
                >
                    <SectionForm
                        form={medicalForm}
                        layout="vertical"
                        onFinish={handleMedicalSubmit}
                        autoComplete="off"
                    >
                        <FormDivider orientation="left" orientationMargin="0">
                            Family Details (As Per Aadhaar)
                        </FormDivider>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <Form.Item
                                label="Spouse Name (As Per Aadhaar)"
                                name="spouseName"
                                rules={[
                                    { pattern: NAME_REGEX, message: 'Please enter valid name!' }
                                ]}
                            >
                                {renderField(4, <IconInput placeholder="Spouse name" prefix={<FaUser />} />)}
                            </Form.Item>
                            <Form.Item
                                label="Spouse Date of Birth"
                                name="spouseDob"
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (getFieldValue('maritalStatus') !== 'married' || value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Please provide spouse date of birth!'));
                                        },
                                    }),
                                ]}
                            >
                                {renderField(4, <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="DD/MM/YYYY" />)}
                            </Form.Item>
                        </div>

                        {[1, 2, 3].map((childNum) => (
                            <div key={childNum} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                                <Form.Item
                                    label={`Child ${childNum} Name (As Per Aadhaar)`}
                                    name={`child${childNum}Name`}
                                    rules={[
                                        { pattern: NAME_REGEX, message: 'Please enter valid name!' }
                                    ]}
                                >
                                    {renderField(4, <FormInput placeholder={`Child ${childNum} name`} />)}
                                </Form.Item>
                                <Form.Item
                                    label={`Child ${childNum} Date of Birth`}
                                    name={`child${childNum}Dob`}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                const childName = getFieldValue(`child${childNum}Name`);
                                                if (!childName || value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error(`Please provide date of birth for Child ${childNum}`));
                                            },
                                        }),
                                    ]}
                                >
                                    {renderField(4, <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="DD/MM/YYYY" />)}
                                </Form.Item>
                            </div>
                        ))}

                        <FormDivider orientation="left" orientationMargin="0">
                            Emergency Contact
                        </FormDivider>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                            <Form.Item
                                label="Name"
                                name="emergencyContactName"
                                rules={[
                                    { required: true, message: 'Please fill this field!' },
                                    { pattern: NAME_REGEX, message: 'Please enter valid name!' }
                                ]}
                            >
                                {renderField(4,
                                    <IconInput placeholder="Name" prefix={<FaAddressCard />} />
                                )}
                            </Form.Item>
                            <Form.Item
                                label="Phone Number"
                                name="emergencyContactPhone"
                                rules={[
                                    { required: true, message: 'Please enter emergency contact number!' },
                                    {
                                        pattern: PHONE_REGEX,
                                        message: 'Please enter valid 10-digit mobile number!'
                                    }
                                ]}
                            >
                                {renderField(4,
                                    <IconInput placeholder="Phone Number" prefix={<FaPhone />} />
                                )}
                            </Form.Item>
                            <Form.Item
                                label="Relationship"
                                name="emergencyContactRelationship"
                                rules={[
                                    { required: true, message: 'Please fill this field!' },
                                    { pattern: NAME_REGEX, message: 'Please enter valid relationship!' }
                                ]}
                            >
                                {renderField(4,
                                    <FormInput placeholder="Relationship" />
                                )}
                            </Form.Item>
                        </div>

                        <Form.Item
                            name="declaration"
                            valuePropName="checked"
                            rules={[
                                {
                                    validator: (_, value) =>
                                        value ? Promise.resolve() : Promise.reject('You must accept the declaration')
                                }
                            ]}
                        >
                            {renderField(4,
                                <Checkbox required>
                                    I hereby declare the above information provided by me to be true. In case the above particulars are found incorrect or it is found that I concealed or withheld some other relevant facts, the company can terminate my employment without any notice.
                                </Checkbox>
                            )}
                        </Form.Item>


                        {!isAllSectionsCompleted() && (
                            <Form.Item>
                                <SubmitButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading.medical}
                                >
                                    {isSectionCompleted(4) ? 'Update Medical Policy Details' : 'Save Medical Policy Details'}
                                </SubmitButton>
                            </Form.Item>
                        )}
                    </SectionForm>
                </Panel>

                {/* Indemnity Bond Section */}
                <Panel
                    header={
                        <PanelHeader>
                            <PanelIcon><FaShieldAlt /></PanelIcon>
                            <span>Indemnity Bond</span>
                            {isSectionCompleted(5) && <Text type="success" style={{ marginLeft: 'auto' }}>Completed</Text>}
                        </PanelHeader>
                    }
                    key="5"
                >
                    <SectionForm
                        form={indemnityForm}
                        layout="vertical"
                        onFinish={handleIndemnitySubmit}
                        autoComplete="off"
                    >
                        <div style={{ marginBottom: 24 }}>
                            <Text strong>INDEMNITY BOND</Text>
                            <div style={{ marginTop: 16 }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                    <Text>This Indemnity Bond executed at New Delhi on this</Text>
                                    <Form.Item
                                        name="indemnityDay"
                                        style={{ margin: '0 8px', width: 80 }}
                                        initialValue={new Date().getDate().toString()}
                                    >
                                        {renderField(5, <Input placeholder="Day" />)}
                                    </Form.Item>

                                    <Text>day of</Text>

                                    <Form.Item
                                        name="indemnityMonth"
                                        style={{ margin: '0 8px', width: 100 }}
                                        initialValue={(new Date().getMonth() + 1).toString()}
                                    >
                                        {renderField(5, <Input placeholder="Month" />)}
                                    </Form.Item>

                                    <Text>year</Text>

                                    <Form.Item
                                        name="indemnityYear"
                                        style={{ margin: '0 8px', width: 80 }}
                                        initialValue={new Date().getFullYear().toString()}
                                    >
                                        {renderField(5, <Input placeholder="Year" />)}
                                    </Form.Item>

                                    <Text>by</Text>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                    <Form.Item
                                        name="indemnityName"
                                        style={{ margin: '0 8px', width: 200 }}
                                        initialValue={mainData.section1?.personalInfo?.legalName || ''}
                                    >
                                        {renderField(5, <Input placeholder="Your Name" />)}
                                    </Form.Item>
                                    <Text>S/O</Text>
                                    <Form.Item
                                        name="indemnityFatherName"
                                        style={{ margin: '0 8px', width: 200 }}
                                        initialValue={mainData.section1?.personalInfo?.fatherName || ''}
                                    >
                                        {renderField(5, <Input placeholder="Father's Name" />)}
                                    </Form.Item>
                                    <Text>R/O having Aadhaar Card number</Text>
                                    <Form.Item
                                        name="aadhaarNumber"
                                        style={{ margin: '0 8px', width: 180 }}
                                        rules={[
                                            { required: true, message: 'Required' },
                                            { pattern: AADHAAR_REGEX, message: 'Invalid Aadhaar' }
                                        ]}
                                    >
                                        {renderField(5, <Input placeholder="Aadhaar Number" />)}
                                    </Form.Item>
                                </div>
                                <Text>
                                    hereinafter referred to as Indemnifier, which expression shall, unless repugnant to or inconsistent with the context, mean and include their executors, administrators, legal representatives and assigns of the one Part and in favour of OXYZO Financial Services Limited.
                                </Text>
                            </div>
                        </div>

                        <div style={{ marginBottom: 24 }}>
                            <Text strong>WHEREAS THIS INDEMNITY BOND WITNESSETH AS FOLLOWS:</Text>
                            <div style={{ marginTop: 16 }}>
                                <ol>
                                    <li>The Indemnifier hereby irrevocably agrees to indemnify OXYZO that in the event of any action taken by the previous Employer with respect to his joining with OXYZO.</li>
                                    <li>The Indemnifier hereby irrevocably agrees to indemnify OXYZO in the event any breach under the Non-Disclosure / Non-Solicitation / Non-Compete Agreement or any other document executed by the Indemnifier with OXYZO. The Indemnity premium amount for the same shall be calculated at the time of actual losses incurred by OXYZO which in no case shall be less than the 5 (Five) times of the last CTC of the Indemnifier.</li>
                                    <li>The Indemnifier understands that a substantial amount of resources and amount would be spent on the Indemnifier for training and other purposes to make Indemnifier employable and in case of early release or request for the resignation has been received by the Indemnifier before the minimum employment tenure promised (if enumerated in the other documents executed between the Indemnifier and OXYZO) by the Indemnifier. The Indemnifier shall pay a premium amount of not less than 2 times of the last CTC.</li>
                                    <li>This Indemnity Bond shall survive any termination/Expiry of Employment services by the Indemnifier and shall be binding till the breach is continuing under any document executed the Indemnifier and OXYZO or till 2 (years) from the last day of Employment (whichever is longer).</li>
                                    <li>Indemnifier hereby confirm that in case of any dispute/difference for settlement of claims under this indemnity bond the courts in Delhi/New Delhi shall have the jurisdiction to decide the rights & liabilities of the parties while adjudicating the matter of claims under this Indemnity Bond.</li>
                                </ol>
                            </div>
                        </div>

                        <Form.Item
                            name="isAgreed"
                            valuePropName="checked"
                            rules={[
                                {
                                    validator: (_, value) =>
                                        value ? Promise.resolve() : Promise.reject('You must accept the terms')
                                }
                            ]}
                        >
                            {renderField(5,
                                <Checkbox>
                                    I have read and understood the contents and terms of this Indemnity Bond and other documents executed by me with OXYZO.
                                </Checkbox>
                            )}
                        </Form.Item>

                        <FormDivider orientation="left" orientationMargin="0">
                            Witness Details
                        </FormDivider>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <Form.Item
                                label="Witness 1 Name"
                                name="witness1Name"
                                initialValue="Tanvi"
                            >
                                {renderField(5,
                                    <Input placeholder="Witness 1 Name" disabled />
                                )}
                            </Form.Item>
                            <Form.Item
                                label="Witness 1 Address"
                                name="witness1Address"
                                initialValue="Gurugram"
                            >
                                {renderField(5,
                                    <Input placeholder="Witness 1 Address" disabled />
                                )}
                            </Form.Item>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <Form.Item
                                label="Witness 2 Name"
                                name="witness2Name"
                                initialValue="Abel Cherian Abey"
                            >
                                {renderField(5,
                                    <Input placeholder="Witness 2 Name" disabled />
                                )}
                            </Form.Item>
                            <Form.Item
                                label="Witness 2 Address"
                                name="witness2Address"
                                initialValue="Gurugram"
                            >
                                {renderField(5,
                                    <Input placeholder="Witness 2 Address" disabled />
                                )}
                            </Form.Item>
                        </div>

                        {!isAllSectionsCompleted() && (
                            <Form.Item>
                                <SubmitButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading.indemnity}
                                >
                                    {isSectionCompleted(5) ? 'Update Indemnity Bond' : 'Save Indemnity Bond'}
                                </SubmitButton>
                            </Form.Item>
                        )}
                    </SectionForm>
                </Panel>

                {/* Non-Disclosure Agreement Section */}
                <Panel
                    header={
                        <PanelHeader>
                            <PanelIcon><FaFileSignature /></PanelIcon>
                            <span>Non-Disclosure Agreement</span>
                            {isSectionCompleted(6) && <Text type="success" style={{ marginLeft: 'auto' }}>Completed</Text>}
                        </PanelHeader>
                    }
                    key="6"
                >
                    <SectionForm
                        form={ndaForm}
                        layout="vertical"
                        onFinish={handleNDASubmit}
                        autoComplete="off"
                    >
                        <div style={{ marginBottom: 24 }}>
                            <Text strong>Non-Disclosure / Non-Solicitation / Non-Compete Agreement</Text>
                            <div style={{ marginTop: 16 }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                    <Text>This Non-Disclosure / Non-Solicitation / Non-Compete Agreement (the "Agreement") is entered and executed at New Delhi on this</Text>
                                    <Form.Item
                                        name="ndaDay"
                                        initialValue={new Date().getDate().toString()}
                                        style={{ margin: '0 8px', width: 80 }}
                                        rules={[
                                            { required: true, message: 'Required' },
                                            { pattern: /^(0?[1-9]|[12][0-9]|3[01])$/, message: 'Invalid day' }
                                        ]}
                                    >
                                        {renderField(6, <Input placeholder="Day" />)}
                                    </Form.Item>
                                    <Text>day of</Text>
                                    <Form.Item
                                        name="ndaMonth"
                                        initialValue={(new Date().getMonth() + 1).toString()}
                                        style={{ margin: '0 8px', width: 100 }}
                                        rules={[
                                            { required: true, message: 'Required' },
                                            { pattern: /^(0?[1-9]|1[0-2])$/, message: 'Invalid month' }
                                        ]}
                                    >
                                        {renderField(6, <Input placeholder="Month" />)}
                                    </Form.Item>
                                    <Text>year</Text>
                                    <Form.Item
                                        name="ndaYear"
                                        initialValue={new Date().getFullYear().toString()}
                                        style={{ margin: '0 8px', width: 80 }}
                                        rules={[
                                            { required: true, message: 'Required' },
                                            { pattern: /^(19|20)\d{2}$/, message: 'Invalid year' }
                                        ]}
                                    >
                                        {renderField(6, <Input placeholder="Year" />)}
                                    </Form.Item>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                    <Text>By and between Mr./Mrs./Ms.</Text>
                                    <Form.Item
                                        name="ndaName"
                                        initialValue={mainData.section1?.personalInfo?.legalName || ''}
                                        style={{ margin: '0 8px', width: 200 }}
                                        rules={[
                                            { required: true, message: 'Required' },
                                            { pattern: NAME_REGEX, message: 'Invalid name' }
                                        ]}
                                    >
                                        {renderField(6, <Input placeholder="Your Name" />)}
                                    </Form.Item>
                                    <Text>S/o, D/o,</Text>
                                    <Form.Item
                                        name="ndaFatherName"
                                        initialValue={mainData.section1?.personalInfo?.fatherName || ''}
                                        style={{ margin: '0 8px', width: 200 }}
                                        rules={[
                                            { required: true, message: 'Required' },
                                            { pattern: NAME_REGEX, message: 'Invalid name' }
                                        ]}
                                    >
                                        {renderField(6, <Input placeholder="Father's Name" />)}
                                    </Form.Item>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                    <Text>having his/her Aadhaar number</Text>
                                    <Form.Item
                                        name="aadhaarNumber"
                                        initialValue={mainData.section5?.aadhaarNumber || ''}
                                        style={{ margin: '0 8px', width: 180 }}
                                        rules={[
                                            { required: true, message: 'Required' },
                                            { pattern: AADHAAR_REGEX, message: 'Invalid Aadhaar' }
                                        ]}
                                    >
                                        {renderField(6, <Input placeholder="Aadhaar Number" />)}
                                    </Form.Item>
                                    <Text>R/O</Text>
                                    <Form.Item
                                        name="ndaAddress"
                                        initialValue={mainData.section1?.addressInfo?.permanentAddress?.pAddress || ''}
                                        style={{ margin: '0 8px', width: 200 }}
                                        rules={[
                                            { required: true, message: 'Required' }
                                        ]}
                                    >
                                        {renderField(6, <Input placeholder="Address" />)}
                                    </Form.Item>
                                    <Text>("Receiving Party") AND M/S OXYZO Financial Services Limited.</Text>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: 24 }}>
                            <Text strong>WHEREAS:</Text>
                            <div style={{ marginTop: 16 }}>
                                <ol>
                                    <li>The Receiving Party has approached the Disclosing Party to provide employment based on the credentials submitted by the Receiving Party.</li>
                                    <li>The Disclosing Party after reviewing all the Credentials submitted by the Receiving Party has agreed to consider the Receiving Party for the Employment as enumerated in the separate documents executed between the Parties or Acknowledged by party for the condition precedent for this Employment.</li>
                                    <li>The Receiving Party understands that this Agreement forms the heart and soul of the Employment and any breach under this Agreement shall cause the Disclosing Party an irreparable injury and loss.</li>
                                </ol>
                            </div>
                        </div>

                        <Form.Item
                            name="isAgreed"
                            valuePropName="checked"
                            rules={[
                                {
                                    validator: (_, value) =>
                                        value ? Promise.resolve() : Promise.reject('You must accept the terms')
                                }
                            ]}
                        >
                            {renderField(6,
                                <Checkbox>
                                    I have read and understood the contents and terms of this Agreement.
                                </Checkbox>
                            )}
                        </Form.Item>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
                            <div style={{ width: '45%' }}>
                                <Form.Item
                                    label="Employee Name"
                                    name="employeeName"
                                    initialValue={mainData.section1?.personalInfo?.legalName || ''}
                                    rules={[
                                        { required: true, message: 'Required' },
                                        { pattern: NAME_REGEX, message: 'Invalid name' }
                                    ]}
                                >
                                    {renderField(6,
                                        <Input placeholder="Employee Name" />
                                    )}
                                </Form.Item>
                            </div>
                            <div style={{ width: '45%' }}>
                                <Form.Item
                                    label="Place"
                                    name="place"
                                    rules={[
                                        { required: true, message: 'Required' }
                                    ]}
                                >
                                    {renderField(6,
                                        <Input placeholder="Place" />
                                    )}
                                </Form.Item>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
                            <div style={{ width: '45%' }}>
                                <Form.Item
                                    label="Designation"
                                    name="ndaDesignation"
                                    rules={[
                                        { required: true, message: 'Required' }
                                    ]}
                                >
                                    {renderField(6,
                                        <Input placeholder="Designation" />
                                    )}
                                </Form.Item>
                            </div>
                            <div style={{ width: '45%' }}>
                                <Form.Item
                                    label="Date"
                                    name="signatureDate"
                                    rules={[
                                        { required: true, message: 'Required' }
                                    ]}
                                >
                                    {renderField(6,
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                            placeholder="DD/MM/YYYY"
                                        />
                                    )}
                                </Form.Item>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>

                            <div style={{ width: '45%' }}>
                                <Form.Item
                                    label="Witness 1 Name"
                                    name="witness1Name"
                                    initialValue="Tanvi"
                                    rules={[
                                        { required: true, message: 'Required' },
                                        { pattern: NAME_REGEX, message: 'Invalid name' }
                                    ]}
                                >
                                    {renderField(6,
                                        <Input placeholder="Witness 1 Name" />
                                    )}
                                </Form.Item>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
                            <div style={{ width: '45%' }}>
                                <Form.Item
                                    label="Witness 2 Name"
                                    name="witness2Name"
                                    initialValue="Abel Cherian Abey"
                                    rules={[
                                        { required: true, message: 'Required' },
                                        { pattern: NAME_REGEX, message: 'Invalid name' }
                                    ]}
                                >
                                    {renderField(6,
                                        <Input placeholder="Witness 2 Name" />
                                    )}
                                </Form.Item>
                            </div>
                        </div>

                        {!isAllSectionsCompleted() && (
                            <Form.Item>
                                <SubmitButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading.nda}
                                >
                                    {isSectionCompleted(6) ? 'Update NDA Agreement' : 'Save NDA Agreement'}
                                </SubmitButton>
                            </Form.Item>
                        )}
                    </SectionForm>
                </Panel>

                {/* Authorization Form Section */}
                <Panel
                    header={
                        <PanelHeader>
                            <PanelIcon><FaHandshake /></PanelIcon>
                            <span>Authorization Form</span>
                            {isSectionCompleted(7) && <Text type="success" style={{ marginLeft: 'auto' }}>Completed</Text>}
                        </PanelHeader>
                    }
                    key="7"
                >
                    <SectionForm
                        form={authForm}
                        layout="vertical"
                        onFinish={handleAuthSubmit}
                        autoComplete="off"
                    >
                        <div style={{ marginBottom: 24 }}>
                            <Text strong>CANDIDATE AUTHORISATION FORM</Text>
                            <div style={{ marginTop: 16 }}>
                                <Text>
                                    I, ______ hereby authorize OXYZO Financial Services Limited or their representatives to verify information presented on my Background Verification Form / resume and to procure an investigative report or consumer report for that purpose.
                                </Text>
                                <Text style={{ display: 'block', marginTop: 16 }}>
                                    I certify that the information furnished in this form filled-in by me in conjunction with my Addresses, details are true to the best of my knowledge.
                                </Text>
                                <Text style={{ display: 'block', marginTop: 16 }}>
                                    I hereby grant authority to the bearer of this letter to access or to be provided with full details.
                                </Text>
                            </div>
                        </div>

                        <Form.Item
                            name="isAgreed"
                            valuePropName="checked"
                            rules={[
                                {
                                    validator: (_, value) =>
                                        value ? Promise.resolve() : Promise.reject('You must accept the authorization')
                                }
                            ]}
                        >
                            {renderField(7,
                                <Checkbox>
                                    I authorize OXYZO Financial Services Limited to verify my background information.
                                </Checkbox>
                            )}
                        </Form.Item>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
                            <div style={{ width: '45%' }}>
                                <Form.Item
                                    label="Name of the Candidate"
                                    name="name"
                                    initialValue={mainData.section1?.personalInfo?.legalName || ''}
                                    rules={[
                                        { required: true, message: 'Required' },
                                        { pattern: NAME_REGEX, message: 'Invalid name' }
                                    ]}
                                >
                                    {renderField(7,
                                        <Input placeholder="Your Name" />
                                    )}
                                </Form.Item>
                            </div>

                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
                            <div style={{ width: '45%' }}>
                                <Form.Item
                                    label="Date"
                                    name="date"
                                    rules={[
                                        { required: true, message: 'Required' }
                                    ]}
                                    initialValue={mainData?.section6?.signatureDate
                                        ? dayjs(mainData.section6.signatureDate)
                                        : dayjs('')}
                                >
                                    {renderField(7,
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                            placeholder="DD/MM/YYYY"
                                        />
                                    )}
                                </Form.Item>
                            </div>
                            <div style={{ width: '45%' }}>
                                <Form.Item
                                    label="Place"
                                    name="place"
                                    rules={[
                                        { required: true, message: 'Required' }
                                    ]}
                                    initialValue={mainData?.section6?.place || ''}
                                >
                                    {renderField(7,
                                        <Input placeholder="Place" />
                                    )}
                                </Form.Item>
                            </div>
                        </div>

                        {!isAllSectionsCompleted() && (
                            <Form.Item>
                                <SubmitButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading.auth}
                                >
                                    {isSectionCompleted(7) ? 'Update Authorization Form' : 'Save Authorization Form'}
                                </SubmitButton>
                            </Form.Item>
                        )}
                    </SectionForm>
                </Panel>
            </CustomCollapse>
        </MainCard>
        </EmployeesContainer >
    );
})

export default JoiningKit;